import { cn } from '@/lib/cn';

/**
 * A data table.
 *
 * The wrapper scrolls, not the page. A wide table that widens <body> puts a
 * horizontal scrollbar under the whole app and pushes the nav off-screen on a
 * phone - so `.table-scroll` is not optional decoration, it is what keeps the
 * layout intact.
 *
 * `.table-scroll` also fades its right edge while there is more to the right,
 * because a table that scrolls and does not say so is a table whose last two
 * columns nobody finds - and on an invoice list those are Total and Balance.
 */
export function Table({ className, ...props }: React.TableHTMLAttributes<HTMLTableElement>) {
  return (
    <div className="table-scroll">
      <table className={cn('w-full caption-bottom text-sm', className)} {...props} />
    </div>
  );
}

export function THead({ className, ...props }: React.HTMLAttributes<HTMLTableSectionElement>) {
  return <thead className={cn('border-b border-border', className)} {...props} />;
}

export function TBody({ className, ...props }: React.HTMLAttributes<HTMLTableSectionElement>) {
  return <tbody className={cn('[&_tr:last-child]:border-0', className)} {...props} />;
}

export function TR({ className, ...props }: React.HTMLAttributes<HTMLTableRowElement>) {
  return (
    <tr
      className={cn('border-b border-border transition-colors hover:bg-muted/50', className)}
      {...props}
    />
  );
}

interface THProps extends React.ThHTMLAttributes<HTMLTableCellElement> {
  /** Right-aligns and tabularises. Every money and count column sets this. */
  numeric?: boolean;
}

/**
 * Cells do not wrap, and the container scrolls.
 *
 * ── WHY THIS IS THE DEFAULT AND NOT AN OPT-IN ──
 * On a phone the invoice table wrapped `05-09-2026` onto THREE lines and
 * `INV-01563` onto two, which made every row three times as tall while still
 * needing a horizontal scroll for the money columns - the worst of both, and it
 * pushed Total and Balance off the edge where nobody would look for them.
 *
 * Wrapping inside a container that already scrolls buys nothing. A date, a
 * document number and an amount are single tokens; breaking them makes them
 * harder to read, not narrower in any useful sense. Cells that genuinely hold
 * prose - a note, an address - opt back in with `wrap`.
 */
interface CellProps {
  /** Let this cell wrap. For prose, not for a figure or a date. */
  wrap?: boolean;
}

export function TH({ className, numeric, wrap, ...props }: THProps & CellProps) {
  return (
    <th
      className={cn(
        'h-9 px-3 text-left align-middle text-[11px] font-medium uppercase tracking-wide text-muted-foreground',
        numeric && 'text-right',
        !wrap && 'whitespace-nowrap',
        className,
      )}
      {...props}
    />
  );
}

interface TDProps extends React.TdHTMLAttributes<HTMLTableCellElement> {
  numeric?: boolean;
}

export function TD({ className, numeric, wrap, ...props }: TDProps & CellProps) {
  return (
    <td
      className={cn(
        'px-3 py-2.5 align-middle',
        numeric && 'text-right tabular',
        !wrap && 'whitespace-nowrap',
        className,
      )}
      {...props}
    />
  );
}
